import React from 'react';
import {
        Box,
        Container,
        Typography,
        Paper,
        Divider,
        List,
        ListItem,
        ListItemText,
        Link
} from '@mui/material';

const PrivacyPolicy: React.FC = () => {
        const lastUpdated = new Date().toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
        });

        return (
                <Container maxWidth="md" sx={{ py: 4 }}>
                        <Typography variant="h3" component="h1" gutterBottom>
                                Privacy Policy
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                Last Updated: {lastUpdated}
                        </Typography>

                        <Paper elevation={0} sx={{ p: 4, mt: 3, border: '1px solid #e0e0e0' }}>
                                <section>
                                        <Typography variant="h5" gutterBottom>
                                                1. Introduction
                                        </Typography>
                                        <Typography paragraph>
                                                Welcome to Executive Force AI ("we," "our," or "us"). We are committed to protecting your privacy and ensuring your personal data is handled in compliance with the California Consumer Privacy Act (CCPA) and applicable US privacy laws.
                                        </Typography>
                                        <Typography paragraph>
                                                This policy explains how we collect, use, and protect your personal information when you use our financial management application.
                                        </Typography>
                                </section>

                                <Divider sx={{ my: 3 }} />

                                <section>
                                        <Typography variant="h5" gutterBottom>
                                                2. Data Controller
                                        </Typography>
                                        <Typography paragraph>
                                                Executive Force AI Inc. is the data controller responsible for your personal data.
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}>
                                                Company Registration Number:16258513 ,ICO Registration Number]
                                        </Typography>
                                </section>

                                <Divider sx={{ my: 3 }} />

                                <section>
                                        <Typography variant="h5" gutterBottom>
                                                3. The Data We Collect
                                        </Typography>
                                        <Typography paragraph>
                                                We may collect, use, store, and transfer different kinds of personal data about you, which we have grouped as follows:
                                        </Typography>
                                        <List dense>
                                                <ListItem>
                                                        <ListItemText
                                                                primary="Identity Data"
                                                                secondary="First name, last name, username, title, and employee ID."
                                                        />
                                                </ListItem>
                                                <ListItem>
                                                        <ListItemText
                                                                primary="Contact Data"
                                                                secondary="Email address, telephone numbers, and business address."
                                                        />
                                                </ListItem>
                                                <ListItem>
                                                        <ListItemText
                                                                primary="Financial Data"
                                                                secondary="Bank account details, transaction records, expenses, and payroll information."
                                                        />
                                                </ListItem>
                                                <ListItem>
                                                        <ListItemText
                                                                primary="Technical Data"
                                                                secondary="Internet protocol (IP) address, login data, browser type and version, time zone setting, and operating system."
                                                        />
                                                </ListItem>
                                                <ListItem>
                                                        <ListItemText
                                                                primary="Usage Data"
                                                                secondary="Information about how you use our application and services, including access logs and audit trails."
                                                        />
                                                </ListItem>
                                        </List>
                                </section>

                                <Divider sx={{ my: 3 }} />

                                <section>
                                        <Typography variant="h5" gutterBottom>
                                                4. Legal Basis for Processing
                                        </Typography>
                                        <Typography paragraph>
                                                We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:
                                        </Typography>
                                        <List sx={{ listStyleType: 'disc', pl: 4 }}>
                                                <ListItem sx={{ display: 'list-item' }}>
                                                        <ListItemText primary="Performance of a Contract: Where we need to perform the contract we are about to enter into or have entered into with you." />
                                                </ListItem>
                                                <ListItem sx={{ display: 'list-item' }}>
                                                        <ListItemText primary="Legal Obligation: Where we need to comply with a legal or regulatory obligation (e.g., IRS tax reporting, financial record keeping)." />
                                                </ListItem>
                                                <ListItem sx={{ display: 'list-item' }}>
                                                        <ListItemText primary="Legitimate Interests: Where it is necessary for our legitimate interests (or those of a third party) and your interests and fundamental rights do not override those interests." />
                                                </ListItem>
                                        </List>
                                </section>

                                <Divider sx={{ my: 3 }} />

                                <section>
                                        <Typography variant="h5" gutterBottom>
                                                5. Data Retention
                                        </Typography>
                                        <Typography paragraph>
                                                We will only retain your personal data for as long as necessary to fulfil the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements.
                                        </Typography>
                                        <Typography paragraph>
                                                By law, we have to keep basic information about our customers and financial transactions for six years plus the current financial year for tax purposes.
                                        </Typography>
                                        <Typography paragraph>
                                                You can view our detailed retention schedules on the <Link href="/data-retention" underline="hover">Data Retention Policy</Link> page.
                                        </Typography>
                                </section>

                                <Divider sx={{ my: 3 }} />

                                <section>
                                        <Typography variant="h5" gutterBottom>
                                                6. Your Legal Rights
                                        </Typography>
                                        <Typography paragraph>
                                                Under certain circumstances, you have rights under data protection laws in relation to your personal data, including the right to:
                                        </Typography>
                                        <List dense>
                                                <ListItem>
                                                        <ListItemText primary="Request access to your personal data (Subject Access Request)." />
                                                </ListItem>
                                                <ListItem>
                                                        <ListItemText primary="Request correction of the personal data that we hold about you." />
                                                </ListItem>
                                                <ListItem>
                                                        <ListItemText primary="Request erasure of your personal data ('Right to be Forgotten')." />
                                                </ListItem>
                                                <ListItem>
                                                        <ListItemText primary="Object to processing of your personal data." />
                                                </ListItem>
                                                <ListItem>
                                                        <ListItemText primary="Request restriction of processing of your personal data." />
                                                </ListItem>
                                                <ListItem>
                                                        <ListItemText primary="Request the transfer of your personal data (Data Portability)." />
                                                </ListItem>
                                        </List>
                                        <Typography paragraph>
                                                If you wish to exercise any of the rights set out above, please contact our Data Protection Officer.
                                        </Typography>
                                </section>

                                <Divider sx={{ my: 3 }} />

                                <section>
                                        <Typography variant="h5" gutterBottom>
                                                7. Contact Us
                                        </Typography>
                                        <Typography paragraph>
                                                If you have any questions about this privacy policy or our privacy practices, please contact us at:
                                        </Typography>
                                        <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
                                                <Typography variant="body1"><strong>Email:</strong> privacy@executiveforceai.com</Typography>
                                                <Typography variant="body1"><strong>Address:</strong> Bedford House, North Bank , Peterborough, Cambridgechire , PE6 7YY</Typography>
                                        </Box>
                                </section>

                                <Divider sx={{ my: 3 }} />

                                <section>
                                        <Typography variant="caption" display="block" color="text.secondary">

                                        </Typography>
                                </section>
                        </Paper>
                </Container>
        );
};

export default PrivacyPolicy;
